import { ActionIcon, friendlyNameForEffector } from "./view-models/utils";
import { ActionViewModel } from "./view-models/ActionViewModel";
import { Button, Paper, Typography } from "@mui/material";
import { MuiIcon } from "common/components/types";
import { RigActivitiesFormViewModel } from "../RigActivitiesFormViewModel";
import { TypedPathTo, useFormikValuesAtBasePath } from "portal/utils/typedPath";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import HoldingActionIcon from "@mui/icons-material/Repeat";
import ImplementNameIcon from "@mui/icons-material/Logout";
import React, { useMemo } from "react";
import StartDelayIcon from "@mui/icons-material/TimerOutlined";

export interface ActionCardDetailProps {
  Icon: MuiIcon;
  text: string;
}

export interface ActionCardProps {
  actionBasePath: TypedPathTo<ActionViewModel<unknown>>;
  stageIndex: number;
  finalActionThisEffector: boolean;
  onEdit: () => void;
}

export const ActionCard = ({
  actionBasePath,
  stageIndex,
  finalActionThisEffector,
  onEdit,
}: ActionCardProps): JSX.Element | undefined => {
  const { t } = useTranslation();
  const { values: formViewModel } =
    useFormikContext<RigActivitiesFormViewModel>();
  const actionViewModel = useFormikValuesAtBasePath(actionBasePath);
  const title = actionViewModel.actionCardTitle(
    t,
    formViewModel.rigEffectiveConfig,
    stageIndex,
    finalActionThisEffector
  );

  const details: ActionCardDetailProps[] = useMemo(() => {
    const details = [
      {
        Icon: ImplementNameIcon,
        text: friendlyNameForEffector(
          t,
          formViewModel.rigEffectiveConfig,
          actionViewModel.effector
        ),
      },
    ];

    if (
      actionViewModel.holdingAction &&
      stageIndex === actionViewModel.startInStageIndex
    ) {
      details.push({
        Icon: HoldingActionIcon,
        text: t(
          "views.autotractor.autonomousActivities.action.cardDetailHolding"
        ),
      });
    }

    if (actionViewModel.startDelaySec !== 0) {
      details.push({
        Icon: StartDelayIcon,
        text: t(
          "views.autotractor.autonomousActivities.action.cardDetailDelayTime",
          {
            count: actionViewModel.startDelaySec,
          }
        ),
      });
    }

    return details;
  }, [
    actionViewModel.effector,
    actionViewModel.holdingAction,
    actionViewModel.startDelaySec,
    actionViewModel.startInStageIndex,
    formViewModel.rigEffectiveConfig,
    stageIndex,
    t,
  ]);

  return title !== undefined ? (
    <Paper
      variant="atk-action-card"
      className="px-4 pt-3 pb-4 rounded-xl flex flex-row items-center gap-1"
    >
      <ActionIcon
        Icon={actionViewModel.components.Icon}
        isHoldingAction={actionViewModel.holdingAction}
        className="mr-2"
      />
      <div className="flex flex-col w-full">
        <div className="flex flex-row gap-1 items-center">
          <Typography variant="subtitle1" className="flex-1">
            {title}
          </Typography>
          <Button
            startIcon={<EditIcon fontSize="inherit" />}
            color="primary"
            variant="contained"
            onClick={() => onEdit()}
          >
            {t("utils.actions.edit")}
          </Button>
        </div>
        <div className="grid grid-cols-[min-content_minmax(0,1fr)] gap-1 items-center w-full">
          {details.map((detail) => (
            <React.Fragment key={detail.text}>
              <detail.Icon color="secondary" />
              <Typography variant="body2" color="secondary">
                {detail.text}
              </Typography>
            </React.Fragment>
          ))}
        </div>
      </div>
    </Paper>
  ) : undefined;
};
